/**
 * Helper per date e formattazione mesi
 */

export const MESI_IT = [
  "Gennaio",
  "Febbraio",
  "Marzo",
  "Aprile",
  "Maggio",
  "Giugno",
  "Luglio",
  "Agosto",
  "Settembre",
  "Ottobre",
  "Novembre",
  "Dicembre",
];

export const MONTHS_IT = MESI_IT; // Alias per compatibilità

/**
 * Ottieni il mese corrente nel formato YYYY-MM
 */
export function getCurrentYearMonth(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
}

/**
 * Formatta YYYY-MM in "Mese Anno" italiano
 */
export function formatYearMonth(ym: string): string {
  const [year, month] = ym.split("-");
  const monthIndex = parseInt(month, 10) - 1;
  return `${MESI_IT[monthIndex]} ${year}`;
}

/**
 * Ottieni il nome del mese in italiano da YYYY-MM
 */
export function getMonthName(ym: string): string {
  const [, month] = ym.split("-");
  const monthIndex = parseInt(month, 10) - 1;
  return MESI_IT[monthIndex];
}

/**
 * Converti da {regione}-{provincia}-{YYYY}-{MM} a YYYY-MM
 */
export function extractYearMonth(filename: string): string | null {
  const match = filename.match(/(\d{4})-(\d{2})\.json$/);
  if (!match) return null;
  return `${match[1]}-${match[2]}`;
}

/**
 * Verifica se un mese è nei prossimi N giorni
 */
export function isWithinDays(ym: string, days: number): boolean {
  const [year, month] = ym.split("-").map(Number);
  const targetDate = new Date(year, month - 1, 1);
  const now = new Date();
  const diffMs = targetDate.getTime() - now.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  return diffDays >= 0 && diffDays <= days;
}

/**
 * Parse YYYY-MM in {year, month}
 */
export function parseYM(ym: string): { year: number; month: number } {
  const [year, month] = ym.split("-").map(Number);
  return { year, month };
}

/**
 * Ottieni nome mese italiano da numero (1-12)
 */
export function monthNameIT(monthNum: number): string {
  return MESI_IT[monthNum - 1] || "Mese";
}

/**
 * Verifica se una data è nello stesso mese di ym
 */
export function isSameMonth(dateStr: string, ym: string): boolean {
  const date = new Date(dateStr);
  const { year, month } = parseYM(ym);
  return date.getFullYear() === year && date.getMonth() + 1 === month;
}

/**
 * Abbreviazioni giorni della settimana in italiano
 */
const GIORNI_ABBR = ["Dom", "Lun", "Mar", "Mer", "Gio", "Ven", "Sab"];

/**
 * Ottieni abbreviazione giorno della settimana (0=Dom, 6=Sab)
 */
function getDayAbbr(date: Date): string {
  return GIORNI_ABBR[date.getDay()] || "";
}

/**
 * Formatta range date per badge card con giorno della settimana
 * Es: "Dom 30 Novembre" o "Lun 15 - Mar 16 Novembre"
 */
export function formatDateRange(startDate: string, endDate?: string): string {
  const start = new Date(startDate);
  const startDay = start.getDate();
  const startMonth = MESI_IT[start.getMonth()]; // Nome completo del mese
  const startDayAbbr = getDayAbbr(start);

  if (!endDate) {
    return `${startDayAbbr} ${startDay} ${startMonth}`;
  }

  const end = new Date(endDate);
  const endDay = end.getDate();

  if (start.getMonth() === end.getMonth()) {
    if (startDay === endDay) {
      // Stesso giorno: mostra solo una volta
      return `${startDayAbbr} ${startDay} ${startMonth}`;
    }
    // Stesso mese, giorni diversi: mostra entrambi i giorni
    const endDayAbbr = getDayAbbr(end);
    return `${startDayAbbr} ${startDay} - ${endDayAbbr} ${endDay} ${startMonth}`;
  }

  // Mesi diversi: mostra entrambi i giorni con i loro mesi
  const endMonth = MESI_IT[end.getMonth()]; // Nome completo del mese
  const endDayAbbr = getDayAbbr(end);
  return `${startDayAbbr} ${startDay} ${startMonth} - ${endDayAbbr} ${endDay} ${endMonth}`;
}

/**
 * Ottieni anno corrente
 */
export function nowY(): number {
  return new Date().getFullYear();
}

/**
 * Genera YYYY-MM da anno e mese
 */
export function ymString(year: number, month: number): string {
  return `${year}-${String(month).padStart(2, "0")}`;
}

/**
 * Formatta range di date in italiano completo
 * es: "11–13 ottobre 2025" o "30 settembre – 2 ottobre 2025"
 */
export function formatRangeIT(startDate: string, endDate?: string): string {
  const start = new Date(startDate);
  const startDay = start.getDate();
  const startMonth = start.getMonth();
  const startYear = start.getFullYear();

  if (!endDate || startDate === endDate) {
    return `${startDay} ${MESI_IT[startMonth].toLowerCase()} ${startYear}`;
  }

  const end = new Date(endDate);
  const endDay = end.getDate();
  const endMonth = end.getMonth();
  const endYear = end.getFullYear();

  // Stesso mese e anno
  if (startMonth === endMonth && startYear === endYear) {
    return `${startDay}-${endDay} ${MESI_IT[startMonth].toLowerCase()} ${startYear}`;
  }

  // Mesi diversi, stesso anno
  if (startYear === endYear) {
    return `${startDay} ${MESI_IT[startMonth].toLowerCase()} - ${endDay} ${MESI_IT[endMonth].toLowerCase()} ${startYear}`;
  }

  // Anni diversi
  return `${startDay} ${MESI_IT[startMonth].toLowerCase()} ${startYear} - ${endDay} ${MESI_IT[endMonth].toLowerCase()} ${endYear}`;
}

/**
 * Formatta range di date per condivisione (senza anno, con barra)
 * es: "15/16 novembre" o "30 novembre/2 dicembre"
 */
export function formatRangeForShare(startDate: string, endDate?: string): string {
  const start = new Date(startDate);
  const startDay = start.getDate();
  const startMonth = start.getMonth();

  if (!endDate || startDate === endDate) {
    return `${startDay} ${MESI_IT[startMonth].toLowerCase()}`;
  }

  const end = new Date(endDate);
  const endDay = end.getDate();
  const endMonth = end.getMonth();

  // Stesso mese
  if (startMonth === endMonth) {
    return `${startDay}/${endDay} ${MESI_IT[startMonth].toLowerCase()}`;
  }

  // Mesi diversi
  return `${startDay} ${MESI_IT[startMonth].toLowerCase()}/${endDay} ${MESI_IT[endMonth].toLowerCase()}`;
}

/**
 * Estrae YYYY-MM da una data ISO
 */
export function toYM(dateStr: string): string {
  const date = new Date(dateStr);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
}
